import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/layout/Header';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Package, Eye, MessageSquare, TrendingUp, Plus, 
  FileText, ChevronRight, Clock, Shield, Star, Edit, Trash2,
  Send, ArrowRight, Crown, CheckCircle, BarChart3
} from 'lucide-react';
import { dashboardApi, productsApi, leadsApi, accountManagerApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { UpgradeBanner } from '../../components/MembershipGating';

const PIPELINE_STAGES = [
  { key: 'new', label: 'New', color: 'bg-blue-500' },
  { key: 'contacted', label: 'Contacted', color: 'bg-yellow-500' },
  { key: 'negotiation', label: 'Negotiation', color: 'bg-purple-500' },
  { key: 'closed_won', label: 'Won', color: 'bg-green-500' },
  { key: 'closed_lost', label: 'Lost', color: 'bg-slate-400' },
];

const SupplierDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accountManager, setAccountManager] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: dashboardData } = await dashboardApi.getSupplier();
        setData(dashboardData);
        try {
          const mgr = await accountManagerApi.getManager();
          setAccountManager(mgr.data);
        } catch {}
      } catch {
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await productsApi.delete(productId);
      toast.success('Product deleted');
      const { data: dashboardData } = await dashboardApi.getSupplier();
      setData(dashboardData);
    } catch {
      toast.error('Failed to delete product');
    }
  };

  const handleLeadStatusChange = async (responseId, newStatus) => {
    try {
      await leadsApi.updateStatus(responseId, newStatus);
      toast.success('Lead status updated');
      const { data: dashboardData } = await dashboardApi.getSupplier();
      setData(dashboardData);
    } catch {
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="container-main py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/4" />
            <div className="grid grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-slate-200 rounded" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const stats = data?.stats || {};
  const products = data?.products || [];
  const inquiries = data?.inquiries || [];
  const matchingRfqs = data?.matching_rfqs || [];
  const sentQuotes = data?.sent_quotes || [];
  const pipeline = data?.pipeline || {};
  const membership = data?.membership || {};

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <div className="container-main py-8" data-testid="supplier-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A192F] mb-1 tracking-tight">Supplier Dashboard</h1>
            <p className="text-slate-500 text-sm">Welcome back, {user?.company_name || user?.name}</p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <Button onClick={() => navigate('/dashboard/products/add')} className="bg-[#EA580C] hover:bg-[#C2410C] rounded-sm font-semibold" data-testid="dashboard-add-product">
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
            <Button variant="outline" onClick={() => navigate(`/suppliers/${user?.user_id}`)} className="rounded-sm">
              View Profile
            </Button>
          </div>
        </div>

        {/* Upgrade Banner for free users */}
        <UpgradeBanner membership={user?.membership} />

        {/* Membership Badge for paid users */}
        {user?.membership && user?.membership !== 'free' && (
          <div className="bg-white border border-slate-200 p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-amber-50 flex items-center justify-center">
                <Crown className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-semibold text-[#0A192F] text-sm">{user.membership?.toUpperCase()} Member</p>
                <p className="text-xs text-slate-500">
                  {membership.expires ? `Expires ${new Date(membership.expires).toLocaleDateString()}` : 'Active membership'}
                </p>
              </div>
            </div>
            <Link to="/membership">
              <Button variant="outline" size="sm" className="rounded-sm text-xs">Manage Plan</Button>
            </Link>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Products', value: stats.products || products.length, icon: Package, color: 'text-[#2563EB]' },
            { label: 'Profile Views', value: stats.views || 0, icon: Eye, color: 'text-[#2563EB]' },
            { label: 'Inquiries', value: stats.inquiries || inquiries.length, icon: MessageSquare, color: 'text-[#EA580C]' },
            { label: 'Response Rate', value: `${stats.response_rate || 0}%`, icon: TrendingUp, color: 'text-[#16A34A]' },
          ].map((stat, i) => (
            <div key={i} className="bg-white border border-slate-200 p-5">
              <div className="flex items-center gap-2 mb-2">
                <stat.icon className={`w-4 h-4 ${stat.color}`} />
                <span className="text-xs text-slate-500 font-medium">{stat.label}</span>
              </div>
              <p className="text-2xl font-bold text-[#0A192F]">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Lead Pipeline */}
        <div className="bg-white border border-slate-200 p-5 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-[#0A192F]">Lead Pipeline</h2>
            <BarChart3 className="w-4 h-4 text-slate-400" />
          </div>
          <div className="grid grid-cols-5 gap-3">
            {PIPELINE_STAGES.map((stage) => (
              <div key={stage.key} className="text-center">
                <div className={`h-2 ${stage.color} rounded-full mb-2`} />
                <p className="text-2xl font-bold text-[#0A192F]">{pipeline[stage.key] || 0}</p>
                <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">{stage.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* New Leads Notification + Account Manager */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {/* New Leads Alert */}
          {(pipeline.new || 0) + matchingRfqs.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 p-5 flex items-start gap-4" data-testid="new-leads-alert">
              <div className="w-10 h-10 rounded-sm bg-blue-500 flex items-center justify-center shrink-0">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-[#0A192F] text-sm mb-1">New Leads Available</h3>
                <p className="text-xs text-slate-600 mb-2">
                  You have <span className="font-bold text-blue-700">{matchingRfqs.length} matching RFQs</span> and
                  {' '}<span className="font-bold text-blue-700">{pipeline.new || 0} new leads</span> in your pipeline.
                </p>
                <Button onClick={() => navigate('/rfqs')} size="sm" className="bg-blue-600 hover:bg-blue-700 rounded-sm text-xs h-7 px-3">
                  View RFQs <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* Account Manager */}
          {accountManager?.has_manager && accountManager.manager && (
            <div className="bg-white border border-slate-200 p-5 flex items-start gap-4" data-testid="account-manager-card">
              <div className="w-10 h-10 rounded-sm bg-[#0A192F] flex items-center justify-center shrink-0">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-[#0A192F] text-sm mb-0.5">Your Account Manager</h3>
                <p className="text-sm text-slate-700 font-medium">{accountManager.manager.name}</p>
                <a href={`mailto:${accountManager.manager.email}`} className="text-xs text-[#EA580C] hover:underline">
                  {accountManager.manager.email}
                </a>
              </div>
            </div>
          )}
          {accountManager && !accountManager.has_manager && accountManager.eligible && (
            <div className="bg-white border border-slate-200 p-5 flex items-start gap-4" data-testid="manager-pending">
              <div className="w-10 h-10 rounded-sm bg-amber-50 flex items-center justify-center shrink-0">
                <Shield className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-[#0A192F] text-sm mb-0.5">Account Manager</h3>
                <p className="text-xs text-slate-500">A dedicated account manager will be assigned to you shortly.</p>
              </div>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Sent Quotes */}
            <div className="bg-white border border-slate-200">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="font-semibold text-[#0A192F]">Sent Quotes</h2>
                <span className="text-xs text-slate-400">{sentQuotes.length} total</span>
              </div>
              {sentQuotes.length > 0 ? (
                <div className="divide-y">
                  {sentQuotes.map((quote) => (
                    <div key={quote.response_id} className="p-4 flex items-center gap-4 hover:bg-slate-50">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#0A192F] text-sm truncate">{quote.rfq_title || quote.rfq_id}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {quote.buyer_name && `Buyer from ${quote.buyer_country || 'International'} · `}${quote.price_quote?.toFixed(2)}/unit · {quote.lead_time}
                        </p>
                      </div>
                      <select
                        value={quote.status}
                        onChange={(e) => handleLeadStatusChange(quote.response_id, e.target.value)}
                        className="text-xs px-2 py-1 rounded-sm border border-slate-200 bg-white text-slate-700"
                        data-testid={`lead-status-${quote.response_id}`}
                      >
                        {PIPELINE_STAGES.map(s => (
                          <option key={s.key} value={s.key}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Send className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No quotes sent yet</p>
                  <Button onClick={() => navigate('/rfqs')} variant="outline" size="sm" className="mt-3 rounded-sm text-xs">
                    Browse RFQs
                  </Button>
                </div>
              )}
            </div>

            {/* Products */}
            <div className="bg-white border border-slate-200">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="font-semibold text-[#0A192F]">Your Products</h2>
                <Link to="/products" className="text-xs text-[#EA580C] hover:underline font-medium">View all</Link>
              </div>
              {products.length > 0 ? (
                <div className="divide-y">
                  {products.map((product) => (
                    <div key={product.product_id} className="p-4 flex items-center gap-4 hover:bg-slate-50">
                      <div className="w-14 h-14 bg-slate-100 overflow-hidden shrink-0">
                        {product.images?.[0] ? (
                          <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Package className="w-5 h-5 text-slate-300" /></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-[#0A192F] text-sm truncate">{product.title}</h3>
                        <p className="text-xs text-slate-500">${product.price_min?.toFixed(2)} - ${product.price_max?.toFixed(2)} · {product.views} views</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => navigate(`/dashboard/products/edit/${product.product_id}`)}><Edit className="w-3.5 h-3.5 text-slate-500" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => handleDeleteProduct(product.product_id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500 mb-3">No products yet</p>
                  <Button onClick={() => navigate('/dashboard/products/add')} size="sm" className="rounded-sm">Add Your First Product</Button>
                </div>
              )}
            </div>

            {/* Matching RFQs */}
            <div className="bg-white border border-slate-200">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="font-semibold text-[#0A192F]">Matching Buy Offers</h2>
                <Link to="/rfqs" className="text-xs text-[#EA580C] hover:underline font-medium">View all</Link>
              </div>
              {matchingRfqs.length > 0 ? (
                <div className="divide-y">
                  {matchingRfqs.map((rfq) => (
                    <Link key={rfq.rfq_id} to={`/rfqs/${rfq.rfq_id}`} className="p-4 flex items-start gap-4 hover:bg-slate-50 block">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-[#0A192F] text-sm">{rfq.title}</h3>
                          {rfq.is_urgent && <Badge className="bg-red-50 text-red-700 border-red-200 text-[10px]">Urgent</Badge>}
                        </div>
                        <p className="text-xs text-slate-500">{rfq.quantity} {rfq.quantity_unit} · {rfq.destination_country}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 mt-1" />
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No matching RFQs found</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Recent Inquiries */}
            <div className="bg-white border border-slate-200">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="font-semibold text-[#0A192F] text-sm">Recent Inquiries</h2>
                <Link to="/dashboard/messages" className="text-xs text-[#EA580C] hover:underline font-medium">View all</Link>
              </div>
              {inquiries.length > 0 ? (
                <div className="divide-y">
                  {inquiries.slice(0, 5).map((inquiry) => (
                    <Link key={inquiry.inquiry_id} to="/dashboard/messages" className="p-4 block hover:bg-slate-50">
                      <p className="font-medium text-[#0A192F] text-sm">{inquiry.buyer_name}</p>
                      <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{inquiry.message}</p>
                      <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" />{new Date(inquiry.created_at).toLocaleDateString()}
                      </p>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center">
                  <MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-xs text-slate-500">No inquiries yet</p>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-white border border-slate-200 p-4">
              <h3 className="font-semibold text-[#0A192F] text-sm mb-3">Quick Actions</h3>
              <div className="space-y-2">
                {[
                  { label: 'Add New Product', icon: Plus, action: () => navigate('/dashboard/products/add') },
                  { label: 'Browse Buy Offers', icon: FileText, action: () => navigate('/rfqs') },
                  { label: 'View Messages', icon: MessageSquare, action: () => navigate('/dashboard/messages') },
                ].map((item, i) => (
                  <Button key={i} variant="outline" className="w-full justify-start rounded-sm text-sm h-9" onClick={item.action}>
                    <item.icon className="w-4 h-4 mr-2" />{item.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Upgrade CTA for free users */}
            {(!user?.membership || user?.membership === 'free') && (
              <div className="bg-[#0A192F] p-6 text-white">
                <Star className="w-7 h-7 text-[#EA580C] mb-3" />
                <h3 className="font-bold text-base mb-1">Upgrade to Gold</h3>
                <p className="text-sm text-slate-300 mb-4 leading-relaxed">
                  Get verified badge, unlimited products, priority RFQ access, and send quotes to buyers.
                </p>
                <Button onClick={() => navigate('/membership')} className="w-full bg-[#EA580C] hover:bg-[#C2410C] rounded-sm font-semibold">
                  View Plans <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierDashboard;
